(async () => {
  // @floating-ui/dom imports won't work without a NODE_ENV defined
  // TODO: maybe check for a better solution to fix this (with rollup).
  // CURRENT USAGE: Uncomment while developing.
  window.process = {
    env: {
      NODE_ENV: 'development',
    },
  };

  // Floating-ui (popper.js successor library): https://floating-ui.com/docs/tutorial
  const { computePosition, autoUpdate, flip, shift, offset } = await import('@floating-ui/dom');

  class FloatUI extends HTMLElement {
    constructor() {
      super();

      this.defaults = {
        strategy: 'absolute',
        onHover: false,
        placement: 'bottom',
        triggerOffset: 8,
        shiftOffset: 8,
        closeOthers: true,
        isContentFocusable: true,
        hideOnWindowScroll: true,
      };

      this.options = {
        ...this.defaults,
        ...(this.dataset.options && JSON.parse(this.dataset.options)),
      };

      this.trigger = this.querySelector('[data-float-ui-trigger]');
      this.target = this.querySelector('[data-float-ui-target]');
      this.isShowing = false;

      // Reduce actions (So the event can also be removed)
      this.reducer = {
        focus: (e) => this.show(e),
        blur: (e) => this.hide(e),
        mousedown: (e) => this.toggle(e),
        mouseEnter: (e) => this.show(e),
        mouseLeave: (e) => this.hide(e),
        clickOutside: (e) => this.onBodyClick(e),
        scroll: (e) => this.onWindowScroll(e),
      };

      this.addEvents();
    }

    /**
     * Add the necessary events
     */
    addEvents() {
      if (this.options.onHover) {
        this.trigger.addEventListener('mouseenter', this.reducer.mouseEnter);
        this.addEventListener('mouseleave', this.reducer.mouseLeave);
      } else {
        this.trigger.addEventListener('click', this.reducer.mousedown);
      }
    }

    /**
     * Toggles the showing/hiding
     */
    toggle(e) {
      if (this.isShowing) {
        this.hide();
      } else {
        this.show(e);
      }
    }

    /**
     * Shows the target
     */
    show(e) {
      e?.preventDefault();

      // Set states
      this.target.ariaHidden = false;
      this.trigger.setAttribute('aria-expanded', true);
      this.isShowing = true;

      // Set position
      this.updateAndCleanup = autoUpdate(this.trigger, this.target, () => {
        computePosition(this.trigger, this.target, {
          strategy: 'fixed',
          placement: this.options.placement,
          middleware: [
            offset(this.options.triggerOffset),
            shift({
              padding: this.options.shiftOffset,
            }),
            flip(),
          ],
        }).then(({x, y}) => {
          Object.assign(this.target.style, {
            left: `${x}px`,
            top: `${y}px`,
          });
        });
      });

      // Add click outside event to body
      document.body.addEventListener('click', this.reducer.clickOutside);

      // Add on window scroll event
      if (this.options.hideOnWindowScroll)
        window.addEventListener('scroll', this.reducer.scroll);

      // Close other floats
      if (this.options.closeOthers) {
        document.querySelectorAll('float-ui')?.forEach((float) => {
          if (float !== this && float.isShowing)
            float.hide();
        });
      }

      // Focus on inner elements if needed
      if (this.options.isContentFocusable && e.type === 'focus') this.trapFocus();
    }

    /**
     * Hides the target
     */
    hide() {
      this.target.ariaHidden = true;
      this.trigger.setAttribute('aria-expanded', false);
      this.trigger.blur();
      this.isShowing = false;

      this.updateAndCleanup();
      document.body.removeEventListener('click', this.reducer.clickOutside);

      if (this.options.isContentFocusable)
        removeTrapFocus(this);
    }

    /**
     * Onclick outside
     * @param {Event} event
     */
    onBodyClick({ target }) {
      if (target !== this && !target.closest('float-ui') && this.isShowing) {
        this.hide();
      }
    }

    /**
     * On window scroll
     */
    onWindowScroll() {
      this.hide();
      window.removeEventListener('scroll', this.reducer.scroll);
    }

    /**
     * Trap the focus inside
     */
    trapFocus() {
      const focusableChildren = this.target.querySelectorAll('a, label, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])');
      const focusableElements = [...focusableChildren].filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));

      if (focusableElements?.length > 0)
        trapFocus(this.target, focusableElements[0]);
    }
  }

  window.FloatUI = FloatUI;
  customElements.define('float-ui', FloatUI);

})();
